import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { ObjectName } from '@view-model';
import { ObjectService } from '@workflow/services/object.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SHARED_IMPORTS } from '@shared/shared-providers';

@Component({
  selector: 'app-object-label',
  standalone: true,
  imports: [SHARED_IMPORTS],
  templateUrl: './object-label.component.html',
  styleUrls: ['./object-label.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ObjectLabelComponent {
  private objectService = inject(ObjectService);

  @Input()
  objectName!: ObjectName;

  @Input()
  objectId!: string;

  @Output()
  remove = new EventEmitter<void>();

  getLabel(): Observable<string> {
    return this.objectService
      .getObject(this.objectName, this.objectId)
      .pipe(map((object) => getObjectLabel(this.objectName, this.objectId, object)));
  }

  onRemove(event: Event): void {
    event.stopPropagation();
    this.remove.emit();
  }
}

const OBJECT_NAME_TO_PROPERTY_GETTERS = new Map<string, ((object: any) => string)[]>([
  ['Contact', [(contact) => contact.firstName, (contact) => contact.lastName]],
  ['Lead', [(lead) => lead.firstName, (lead) => lead.lastName]],
  ['Workplace', [(workplace) => workplace.name]],
  ['Group', [(group) => group.name]],
  ['Specialization', [(specialization) => specialization.name]],
  ['MessageCampaign', [(messageCampaign) => messageCampaign.name]],
  [
    'Representative',
    [(representative) => representative.firstName, (representative) => representative.lastName],
  ],
  ['Meeting', [(meeting) => meeting.title]],
  ['Presentation', [(presentation) => presentation.name]],
  [
    'Message',
    [
      (message) => message.campaign && message.campaign.name,
      () => ' - ',
      (message) => message.receiver && message.receiver.firstName,
      (message) => message.receiver && message.receiver.lastName,
    ],
  ],
  ['MessageElement', [(messageElement) => messageElement.name]],
]);

function getObjectLabel(objectName: ObjectName, objectId: string, object: any): string {
  if (!object) {
    return objectId;
  }

  const propertyGetters = OBJECT_NAME_TO_PROPERTY_GETTERS.get(objectName as string) || [];
  const properties = propertyGetters
    .map((getter) => getter(object))
    .filter((property) => !!property);
  return properties.join(' ') || objectId;
}
